import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Mail, Send } from "lucide-react";
import { submitToWeb3Forms } from "../lib/forms";

export function RequestDialog({
    isOpen,
    onOpenChange,
    config = {},
    context = ""
}) {
    const [email, setEmail] = useState("");
    const [name, setName] = useState("");
    const [phone, setPhone] = useState("");
    const [message, setMessage] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const isFullForm = config.formType === "full";

    // Load from localStorage on mount
    useEffect(() => {
        if (isOpen) {
            setEmail(localStorage.getItem("lexceed_user_email") || "");
            setName(localStorage.getItem("lexceed_user_name") || "");
            setPhone(localStorage.getItem("lexceed_user_phone") || "");
            setSubmitted(false); // Reset on open
        }
    }, [isOpen]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            await submitToWeb3Forms({
                name,
                email,
                phone,
                message,
                context: context || config.subject || "General Inquiry",
            }, {
                from_name: "Lexceed Website Request",
                subject: `New Request: ${context || config.subject || "Information"}`,
            });

            // Save to localStorage
            localStorage.setItem("lexceed_user_email", email);
            localStorage.setItem("lexceed_user_name", name);
            localStorage.setItem("lexceed_user_phone", phone);

            toast.success("Request Sent Successfully", {
                description: `We've received your request for: ${context || config.subject || "Information"}. Our team will get back to you shortly.`,
            });

            setMessage("");
            setSubmitted(true);
            // onOpenChange(false); // Don't close immediately anymore
        } catch (error) {
            toast.error("Submission Error", {
                description: error.message || "Something went wrong. Please try again later.",
            });
        } finally {
            setIsSubmitting(false); // Handled for all outcomes
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[425px] bg-black/80 border-gold/30 backdrop-blur-xl text-white">
                <DialogHeader>
                    <DialogTitle className="text-2xl font-serif font-bold text-gold">
                        {config.title || "Request Information"}
                    </DialogTitle>
                    <DialogDescription className="text-gray-400 mt-2">
                        {config.description || "Please provide your details below to proceed with your request."}
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="space-y-4 py-4">
                    {submitted ? (
                        <div className="py-8 text-center animate-in fade-in zoom-in duration-300">
                            <div className="w-16 h-16 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-6">
                                <Send size={32} className="text-gold" />
                            </div>
                            <h3 className="text-2xl font-serif font-bold text-white mb-2">Message Sent!</h3>
                            <p className="text-gray-400 mb-8">
                                Thank you for reaching out. We've received your request and will get back to you shortly.
                            </p>
                            <Button
                                type="button"
                                onClick={() => onOpenChange(false)}
                                className="w-full bg-gold hover:bg-gold-light text-black font-bold h-11"
                            >
                                Close
                            </Button>
                        </div>
                    ) : (
                        <>
                            {/* Name Field - Always shown, but optional in minimal */}
                            <div className="space-y-2">
                                <Label htmlFor="name" className="text-gold/80 text-xs uppercase tracking-widest">
                                    Full Name {isFullForm && "*"}
                                </Label>
                                <Input
                                    id="name"
                                    required={isFullForm}
                                    placeholder="John Doe"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    className="bg-white/5 border-gold/20 focus:border-gold/50 h-11 text-white placeholder:text-gray-600"
                                />
                            </div>

                            <div className="grid grid-cols-1 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="email" className="text-gold/80 text-xs uppercase tracking-widest">
                                        Email Address *
                                    </Label>
                                    <div className="relative">
                                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gold/40 w-4 h-4" />
                                        <Input
                                            id="email"
                                            type="email"
                                            required
                                            placeholder="you@example.com"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            className="bg-white/5 border-gold/20 focus:border-gold/50 pl-10 h-11 text-white placeholder:text-gray-600"
                                        />
                                    </div>
                                </div>

                                {/* Phone Field - Only shown in full form */}
                                {isFullForm && (
                                    <div className="space-y-2">
                                        <Label htmlFor="phone" className="text-gold/80 text-xs uppercase tracking-widest">
                                            Phone Number *
                                        </Label>
                                        <Input
                                            id="phone"
                                            type="tel"
                                            required
                                            placeholder="+91 98XXX XXXXX"
                                            value={phone}
                                            onChange={(e) => setPhone(e.target.value)}
                                            className="bg-white/5 border-gold/20 focus:border-gold/50 h-11 text-white placeholder:text-gray-600"
                                        />
                                    </div>
                                )}
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="message" className="text-gold/80 text-xs uppercase tracking-widest">
                                    Message (Optional)
                                </Label>
                                <Textarea
                                    id="message"
                                    placeholder={isFullForm ? "How can we help you?" : "Any specific requirements or context..."}
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                    className="bg-white/5 border-gold/20 focus:border-gold/50 min-h-[80px] text-white placeholder:text-gray-600"
                                />
                            </div>

                            <p className="text-[10px] text-gray-500 italic">
                                * Your details are saved locally for your convenience in future requests.
                            </p>

                            <DialogFooter className="pt-4">
                                <Button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="w-full bg-gradient-to-r from-gold to-gold-light text-black font-bold h-12 hover:shadow-lg hover:shadow-gold/30 transition-all duration-300"
                                >
                                    {isSubmitting ? (
                                        <span className="flex items-center">
                                            <span className="animate-spin mr-2">◌</span>
                                            Sending...
                                        </span>
                                    ) : (
                                        <span className="flex items-center">
                                            <Send className="w-4 h-4 mr-2" />
                                            {config.buttonText || "Send Request"}
                                        </span>
                                    )}
                                </Button>
                            </DialogFooter>
                        </>
                    )}
                </form>
            </DialogContent>
        </Dialog>
    );
}
